import { useEffect, useRef, useState } from "react";

/** Cap live SSE progress so completion can finish at 99% → 100% on the client. */
export const AI_PROGRESS_STREAM_CAP = 98;

export const AI_PROGRESS_FINISH_HOLD_MS = 1000;
export const AI_PROGRESS_COMPLETE_FLASH_MS = 400;
export const AI_PROGRESS_PRE_FINISH_RAMP_MS = 500;

export const sleep = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

/**
 * Client-side finale: ramp → 99% → hold → 100% → brief flash before results.
 * @param {function} setProgress - state setter for aiProgressModal
 */
export const runAiProgressFinishSequence = async (setProgress, options = {}) => {
  const {
    holdMs = AI_PROGRESS_FINISH_HOLD_MS,
    flashMs = AI_PROGRESS_COMPLETE_FLASH_MS,
    rampMs = AI_PROGRESS_PRE_FINISH_RAMP_MS,
    error = null,
  } = options;

  setProgress((prev) => ({
    ...prev,
    percent: Math.max(prev.percent, 92),
    stage: "finalising",
    label: error ? "Wrapping up" : "Finalising analyse report",
    error: null,
    errorCode: null,
  }));

  await sleep(rampMs);

  setProgress((prev) => ({
    ...prev,
    percent: 99,
    stage: "finalising",
    label: error ? "Analyse could not complete" : "Almost done…",
  }));

  await sleep(holdMs);

  if (error) {
    setProgress((prev) => ({
      ...prev,
      percent: 100,
      stage: "finalising",
      error,
      errorCode: options.errorCode || null,
    }));
    return;
  }

  setProgress((prev) => ({
    ...prev,
    percent: 100,
    stage: "complete",
    label: "Analyse complete",
  }));

  await sleep(flashMs);
};

/**
 * Smooth displayed % toward the target from SSE/parent state.
 * Adds gentle idle creep so the bar keeps moving during long LLM waits.
 */
export const useAiProgressDisplay = (targetPercent, isActive) => {
  const [displayPercent, setDisplayPercent] = useState(0);
  const targetRef = useRef(0);
  const creepRef = useRef(4);
  const frameRef = useRef(null);

  useEffect(() => {
    targetRef.current = Math.max(0, Math.min(100, Number(targetPercent) || 0));
  }, [targetPercent]);

  useEffect(() => {
    if (!isActive) {
      creepRef.current = 4;
      setDisplayPercent(0);
      return undefined;
    }

    creepRef.current = 4;
    setDisplayPercent(4);

    const tick = () => {
      setDisplayPercent((current) => {
        const serverTarget = targetRef.current;
        const cappedCreep = Math.min(94, creepRef.current);

        if (serverTarget > cappedCreep + 1) {
          creepRef.current = serverTarget;
        } else if (serverTarget < 98) {
          creepRef.current = Math.min(94, cappedCreep + 0.06);
        }

        const effectiveTarget = Math.max(serverTarget, creepRef.current);
        const goal =
          serverTarget > AI_PROGRESS_STREAM_CAP
            ? serverTarget
            : Math.min(effectiveTarget, AI_PROGRESS_STREAM_CAP);

        if (Math.abs(current - goal) < 0.25) {
          return goal;
        }

        const delta = goal - current;
        const distance = Math.abs(delta);
        const finishing = serverTarget > AI_PROGRESS_STREAM_CAP;
        const step = finishing
          ? Math.max(0.65, distance * 0.18)
          : Math.max(0.12, distance * 0.07);
        return current + Math.sign(delta) * Math.min(step, distance);
      });

      frameRef.current = window.requestAnimationFrame(tick);
    };

    frameRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [isActive]);

  return Math.round(displayPercent * 10) / 10;
};
